import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import {
  GET_CUSTOMER_ORDERS_QUERY_KEY,
  invalidateOrders,
  invalidateOrdersV3,
  useNextOrderV3,
  type OrderIngredient,
} from '@hungryroot/orders';
import {
  type Cart,
  type CartProduct,
  type OrderDetail,
  type PairingBundle,
  REACT_QUERY_KEYS,
} from '@hungryroot/rest';
import { getCartFromCache } from '@hungryroot/rest';
import {
  segmentAnalytics,
  pairingInteraction,
  productInteraction,
  type Location,
} from '@hungryroot/segment';
import {
  convertProductToCartProduct,
  convertPairingToCartPairing,
  convertOrderIngredientToCartProduct,
} from '../../transforms';
import { MutationTypes } from '../../types/MutationTypes';
import { type RemoveIngredientMutation } from '../../types/RemoveIngredientMutation';
import { type UseCartMutations } from '../../types/UseCartMutations';
import {
  getBundleDiffBetweenCarts,
  getCartPairingBundle,
  getCartProductBundle,
  getLatestCartPairingBundle,
} from '../../utils/query';
import { updateCart } from '../../utils/updateCart';
import { CartChangeType, executeChangeHandlers } from '../useCartEvents';
import {
  useCartMutationStateMachine,
  type MutationStateMachine,
} from './mutation-state';
import {
  addOptimisticCartProduct,
  addPairingOptimisticCart,
  bundleIsOptimistic,
} from './optimistic';
import { useAddPairingSelf } from './useAddPairingSelf';
import { useAddProductSelf } from './useAddProductSelf';
import { useDeleteBundleSelf } from './useDeleteBundleSelf';
import { useDeleteCartProductSelf } from './useDeleteCartProductSelf';
import { useErrorMessageToasts } from './useErrorMessageToasts';
import { usePatchBundleSelf } from './usePatchBundleSelf';

function onMutate(queryClient: QueryClient, fsm: MutationStateMachine) {
  fsm.recordMutationStarted();
  queryClient.cancelQueries({ queryKey: [REACT_QUERY_KEYS.CART] });

  return getCartFromCache(queryClient);
}

async function onMutationSettled(
  queryClient: QueryClient,
  fsm: MutationStateMachine,
  cart: Cart | undefined
) {
  const state = fsm.recordMutationEnded();
  const { mutationsInFlight, refreshCart } = state;
  if (mutationsInFlight > 0) {
    // There are still mutations going, don't update the cache
    return;
  }

  queryClient.invalidateQueries({
    queryKey: GET_CUSTOMER_ORDERS_QUERY_KEY,
  });
  invalidateOrders(queryClient);
  invalidateOrdersV3(queryClient, { time_bucket: 'upcoming' });

  if (refreshCart === false) {
    if (cart !== undefined) {
      // it's safe to use the HTTP response for the cart, no possibility of conflict
      updateCart(queryClient, cart);
    }
    // An error response settled, the cart has already been rolled back
    return;
  }

  // Invalidate the cart, there were multiple mutations in flight
  // We have to just refresh the whole thing.
  try {
    /**
     * The try/catch attempts to handle an unhandled rejection of `Error: Missing queryFn: '["Cart"]'` in tests.
     * This GH issue is the most relevant: https://github.com/TanStack/query/issues/2096#issuecomment-880547144
     */
    await queryClient.fetchQuery({ queryKey: [REACT_QUERY_KEYS.CART] });
  } catch {}
}

function onMutationError(
  queryClient: QueryClient,
  context: { previousCart: Cart | null } | undefined
) {
  if (context === undefined || context.previousCart === null) {
    return;
  }
  const { previousCart } = context;
  updateCart(queryClient, previousCart);
}

export function useCartMutations(): UseCartMutations {
  const client = useQueryClient();
  const handleErrorWithToast = useErrorMessageToasts();
  const { order } = useNextOrderV3();

  const cartMutationState = useCartMutationStateMachine();
  const addProductMutation = useAddProductSelf({
    onMutate(params: { product: CartProduct; location?: Location }) {
      const { product, location } = params;
      productInteraction({
        productId: product.id,
        actionType: 'added_to_cart',
        productName: product.name,
        location,
        orderId: order?.id,
      });

      const cart = onMutate(client, cartMutationState);
      if (cart !== null) {
        const nextCart = addOptimisticCartProduct(cart, product, {
          addVolume: true,
        });

        updateCart(client, nextCart);
      }

      return {
        previousCart: cart,
      };
    },
    onSettled(data) {
      onMutationSettled(client, cartMutationState, data);
    },
    onError(error, variables, context) {
      onMutationError(client, context);
      handleErrorWithToast(error);
    },
  });

  const addPairingMutation = useAddPairingSelf({
    onMutate(params) {
      const { pairing, ingredients, location } = params;

      pairingInteraction({
        pairingId: pairing.id,
        pairingName: pairing.name,
        actionType: 'added_to_cart',
        location,
        orderId: order?.id,
      });

      const cart = onMutate(client, cartMutationState);
      if (cart !== null) {
        const nextCart = addPairingOptimisticCart(cart, pairing, ingredients, {
          addVolume: true,
        });
        updateCart(client, nextCart);
      }

      return {
        previousCart: cart,
      };
    },
    onSettled(data) {
      onMutationSettled(client, cartMutationState, data);
    },
    onError(error: unknown, variables, context) {
      onMutationError(client, context);
      handleErrorWithToast(error);
    },
  });
  const deleteCartBundleMutation = useDeleteBundleSelf({
    onMutate(params) {
      const { bundleId, pairingId, location } = params;

      pairingInteraction({
        pairingId: params.pairingId,
        pairingName: params.pairingName,
        actionType: 'removed_from_cart',
        location,
        orderId: order?.id,
      });

      const cart = onMutate(client, cartMutationState);
      let deletedBundle: PairingBundle | null = null;
      if (cart !== null) {
        deletedBundle = getCartPairingBundle(cart, pairingId);

        const nextCart = produce(cart, (draft) => {
          draft.bundles = draft.bundles.filter((bundle) => {
            return bundle.id !== bundleId;
          });
        });

        updateCart(client, nextCart);
      }

      return {
        previousCart: cart,
        bundle: deletedBundle,
      };
    },
    onSettled(data) {
      onMutationSettled(client, cartMutationState, data);
    },
    onError(error, variables, context) {
      onMutationError(client, context);
      handleErrorWithToast(error);
    },
  });
  const deleteCartProductMutation = useDeleteCartProductSelf({
    onMutate(params) {
      const { bundleId, productId } = params;
      const cart = onMutate(client, cartMutationState);
      let deletedProduct: CartProduct | null = null;
      if (cart !== null) {
        const matchDeletedProduct = cart.products.find((product) => {
          return product.id === productId;
        });

        if (matchDeletedProduct !== undefined) {
          deletedProduct = matchDeletedProduct;
        }

        const nextCart = produce(cart, (draft) => {
          const bundle = draft.bundles.find((bundle) => {
            return bundle.id === bundleId;
          });

          if (bundle === undefined) {
            // Return if no bundle with matching ID is found.
            // With the change to the onSubmitUpdate function in the Cart,
            // we are now awaiting each successful request, so we want to
            // opt out of the optimistic update
            return;
          }

          const order_details: OrderDetail[] = [];
          let removed = false;
          bundle.order_details.forEach((orderDetail) => {
            const { product: orderDetailId } = orderDetail;
            if (orderDetailId === productId && removed === false) {
              removed = true;
              return;
            }
            order_details.push(orderDetail);
          });
          bundle.order_details = order_details;
        });

        updateCart(client, nextCart);
      }
      return {
        previousCart: cart,
        product: deletedProduct,
      };
    },
    onSettled(data) {
      onMutationSettled(client, cartMutationState, data);
    },
    onError(error, variables, context) {
      onMutationError(client, context);
      handleErrorWithToast(error);
    },
  });

  const patchCartBundleMutation = usePatchBundleSelf({
    onSuccess(nextCart) {
      updateCart(client, nextCart);
    },
  });

  const addCartPairing: UseCartMutations['addCartPairing'] = (params) => {
    const { pairing, ingredients, modifications, location } = params;

    const removeModifications = modifications?.filter(({ type }) => {
      return type === MutationTypes.RemoveIngredient;
    }) as RemoveIngredientMutation[];

    addPairingMutation.mutate(
      { pairing, ingredients, modifications: removeModifications, location },
      {
        onSuccess(cart, _, context) {
          const bundle = getBundleDiffBetweenCarts(
            cart,
            context.previousCart,
            pairing
          );
          executeChangeHandlers({
            type: CartChangeType.AddRecipe,
            bundle: bundle === undefined ? null : bundle,
            pairing: pairing,
          });
        },
      }
    );
  };

  const removeRecipeFromBundle: UseCartMutations['removeRecipeFromBundle'] = (
    params
  ) => {
    const { bundleId, recipeId, pairingName, location } = params;

    if (bundleIsOptimistic({ id: bundleId })) {
      // don't try to delete the product if it's bundle is optimistic
      return;
    }

    deleteCartBundleMutation.mutate(
      {
        bundleId,
        pairingId: recipeId,
        pairingName,
        location,
      },
      {
        onSuccess(data, params, context) {
          const { bundle } = context;
          executeChangeHandlers({
            type: CartChangeType.RemoveRecipe,
            bundle,
          });
        },
      }
    );
  };

  const addCartProduct: UseCartMutations['addCartProduct'] = (params) => {
    const { product, location } = params;

    addProductMutation.mutate(
      { product, location },
      {
        onSuccess() {
          const category = product.categories?.[0]?.name;

          segmentAnalytics.track('Product Added - Web', {
            cart_id: order?.id,
            product_id: product.id,
            category,
          });
          executeChangeHandlers({
            type: CartChangeType.AddProduct,
            product,
          });
        },
      }
    );
  };

  const removeProductFromBundle: UseCartMutations['removeProductFromBundle'] =
    async (params) => {
      const { bundleId, productId, reasonId, location } = params;
      if (bundleIsOptimistic({ id: bundleId })) {
        // don't try to delete the product if it's bundle is optimistic
        return Promise.resolve();
      }

      productInteraction({
        productId,
        actionType: 'removed_from_cart',
        productName: params.productName ?? '',
        location,
        orderId: order?.id,
      });

      await deleteCartProductMutation.mutateAsync(
        {
          bundleId,
          productId,
          reasonId,
        },
        {
          onSuccess(data, params, context) {
            const { product } = context;
            executeChangeHandlers({
              type: CartChangeType.RemoveProduct,
              product,
            });
          },
        }
      );
    };

  return {
    isMutating:
      addProductMutation.status === 'pending' ||
      addPairingMutation.status === 'pending' ||
      deleteCartBundleMutation.status === 'pending' ||
      deleteCartProductMutation.status === 'pending' ||
      patchCartBundleMutation.status === 'pending',
    isMutationSuccessful:
      addProductMutation.status === 'success' ||
      addPairingMutation.status === 'success' ||
      deleteCartBundleMutation.status === 'success' ||
      deleteCartProductMutation.status === 'success' ||
      patchCartBundleMutation.status === 'success',
    addCartPairing,
    removeRecipeFromBundle,
    removeProductFromBundle,
    addCartProduct,
    async swapRecipe(params) {
      const { bundleId, recipe } = params;
      const { id: recipeId } = recipe;

      await patchCartBundleMutation.mutateAsync({
        bundleId,
        pairingId: recipeId,
      });
    },

    async addProductsToBundle(params) {
      const { bundleId, productIds } = params;

      for (const productId of productIds) {
        productInteraction({
          productId,
          actionType: 'added_to_cart',
          location: 'rdp',
          orderId: order?.id,
        });

        await patchCartBundleMutation.mutateAsync({
          bundleId,
          productId,
        });
      }
    },

    // This function handles adding pairings for places *OUTSIDE* the cart
    addPairing({ pairing, location }) {
      const ingredientsMap = pairing.ingredients.reduce(
        (seed, ingredient) => {
          seed[ingredient.id] = ingredient;
          return seed;
        },
        {} as Record<string, OrderIngredient>
      );
      const cartPairing = convertPairingToCartPairing(pairing);
      const ingredients = cartPairing.ingredients.map(
        ({ id: ingredientId }) => {
          const ingredient = ingredientsMap[ingredientId];
          return convertOrderIngredientToCartProduct(ingredient);
        }
      );

      addCartPairing({
        pairing: cartPairing,
        ingredients,
        location,
      });
    },

    // This function handles removing PAIRINGS for places *OUTSIDE* the cart
    removePairing({ pairing, location }) {
      const { id: pairingId } = pairing;
      const cart = getCartFromCache(client);
      if (cart === null) {
        return;
      }
      const bundle = getLatestCartPairingBundle(cart, pairingId);
      const { id: bundleId } = bundle;
      removeRecipeFromBundle({
        bundleId,
        recipeId: pairingId,
        pairingName: pairing.name,
        location,
      });
    },

    // This function handles adding products for places *OUTSIDE* the cart
    addProduct({ product, location }) {
      addCartProduct({
        product: convertProductToCartProduct(product),
        location,
      });
    },

    // This function handles removing products for places *OUTSIDE* the cart
    removeProduct({ product, location }) {
      const { id: productId } = product;
      const cart = getCartFromCache(client);
      if (cart === null) {
        return;
      }
      const productBundle = getCartProductBundle(cart, productId);
      if (productBundle !== undefined) {
        const { id: productBundleId } = productBundle;
        removeProductFromBundle({
          bundleId: productBundleId,
          productId: productId,
          productName: product.name,
          location,
        });
      }
    },
  };
}
